import { z } from "zod";
import { betaFeatureKeySchema } from "./beta-features-contracts.js";
import { ORGANIZATION_MEMBER_ROLES, ORGANIZATION_STATUSES } from "./constants.js";
import { apiIntIdSchema } from "./ids.js";
import { pendingInvitationSchema } from "./team-contracts.js";
/** Browser OAuth presentation mode for Google sign-in. */
export const googleAuthModeSchema = z.enum(["popup", "redirect"]);
/**
 * Safe postMessage payloads from the Google OAuth popup callback.
 * Never include Session tokens, Google tokens, codes, or provider error text.
 */
export const GOOGLE_AUTH_SUCCESS_TYPE = "MULTI_CHAT_GOOGLE_AUTH_SUCCESS";
export const GOOGLE_AUTH_ERROR_TYPE = "MULTI_CHAT_GOOGLE_AUTH_ERROR";
export const googleAuthSuccessMessageSchema = z.object({
    type: z.literal(GOOGLE_AUTH_SUCCESS_TYPE),
});
export const googleAuthErrorMessageSchema = z.object({
    type: z.literal(GOOGLE_AUTH_ERROR_TYPE),
    code: z.string().min(1),
});
export const googleAuthPopupMessageSchema = z.union([
    googleAuthSuccessMessageSchema,
    googleAuthErrorMessageSchema,
]);
/** Whether the requester has a valid application Session cookie. */
export const authSessionResponseSchema = z.object({
    authenticated: z.boolean(),
    user: z
        .object({
        id: apiIntIdSchema,
        email: z.string().email(),
        displayName: z.string(),
    })
        .nullable(),
});
/**
 * How the caller reaches an organization. PLATFORM_ADMIN is effective OWNER
 * without an OrganizationMember; it wins over the user's own membership.
 */
export const ORGANIZATION_ACCESS_KINDS = ["MEMBER", "PLATFORM_ADMIN"];
export const currentUserOrganizationSchema = z.object({
    id: apiIntIdSchema,
    name: z.string(),
    /** Effective role. Always OWNER for PLATFORM_ADMIN access. */
    role: z.enum(ORGANIZATION_MEMBER_ROLES),
    /** Null for PLATFORM_ADMIN access. */
    membershipId: apiIntIdSchema.nullable(),
    access: z.enum(ORGANIZATION_ACCESS_KINDS).default("MEMBER"),
    /** OrganizationMember.displayName, or User.displayName when the member name is unset. */
    memberDisplayName: z.string(),
    /**
     * The caller's real OrganizationMember role, for display only. Present on
     * `/me` memberships; absent for organizations entered as Platform Admin.
     */
    membershipRole: z.enum(ORGANIZATION_MEMBER_ROLES).optional(),
    status: z.enum(ORGANIZATION_STATUSES),
});
export const currentUserResponseSchema = z.object({
    id: apiIntIdSchema,
    email: z.string().email(),
    displayName: z.string(),
    /** Active PlatformAdmin. Read from the database on each `/me`. */
    platformAdmin: z.boolean().default(false),
    /** The user's memberships; for a Platform Admin, shown with PLATFORM_ADMIN access. */
    organizations: z.array(currentUserOrganizationSchema),
    /** Open invitations for the authenticated email. Discovery does not require the raw token. */
    pendingInvitations: z.array(pendingInvitationSchema).default([]),
});
export const organizationSummarySchema = z.object({
    id: apiIntIdSchema,
    name: z.string(),
    role: z.enum(ORGANIZATION_MEMBER_ROLES),
    membershipId: apiIntIdSchema.nullable(),
    access: z.enum(ORGANIZATION_ACCESS_KINDS).default("MEMBER"),
    memberDisplayName: z.string(),
});
export const organizationDetailsSchema = z.object({
    id: apiIntIdSchema,
    name: z.string(),
    timezone: z.string(),
    status: z.enum(ORGANIZATION_STATUSES),
    role: z.enum(ORGANIZATION_MEMBER_ROLES),
    membershipId: apiIntIdSchema.nullable(),
    access: z.enum(ORGANIZATION_ACCESS_KINDS).default("MEMBER"),
    memberDisplayName: z.string(),
    whatsappEnabled: z.boolean(),
    facebookEnabled: z.boolean(),
    instagramEnabled: z.boolean(),
    /** Feature keys currently enabled for this organization under Beta rules. */
    enabledBetaFeatures: z.array(betaFeatureKeySchema).default([]),
});
export const createOrganizationRequestSchema = z.object({
    name: z.string().min(1).max(200),
});
export const createOrganizationResponseSchema = organizationDetailsSchema;
export const updateOrganizationRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    timezone: z.string().min(1).optional(),
})
    .refine((value) => value.name !== undefined || value.timezone !== undefined, {
    message: "At least one of name or timezone is required",
});
export const organizationListResponseSchema = z.object({
    items: z.array(organizationSummarySchema),
});
//# sourceMappingURL=auth-contracts.js.map